import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";
import { RGB, rgb } from "pdf-lib";

import { customToast } from "./custom-toast";

import { filterItem } from "@/hooks/useGetFilter";
import { SocketData } from "@/interfaces/globals";
import { env } from "@/lib/env";
import {
  TCurrency,
  getCurrencySymbol,
  getRootUserId,
  isCustomerPortal,
} from "@/lib/utils";

export const getToken = (): string | null => localStorage.getItem("token");

export const getSubdomain = (): string | null =>
  localStorage.getItem("subdomain");

const workspaceData = JSON.parse(
  localStorage.getItem("workspaceData") || "null",
);

export const getWorkspace = (): string => {
  const id = JSON.parse(localStorage.getItem("workspace") || "null");
  if (!id) {
    return workspaceData?.id;
  }

  return id;
};

export const getCustomerId = (): string => {
  const customerDataRaw = localStorage.getItem("customer-data");

  if (!customerDataRaw) {
    // If "customer-data" is not found, remove it and return an empty string
    localStorage.removeItem("customer-data");
    if (isCustomerPortal) {
      localStorage.clear();
    }

    return "";
  }

  return JSON.parse(customerDataRaw).id;
};

export const BaseWithAuth = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> =>
  fetchBaseQuery({
    baseUrl: env.BACKEND_URL,
    timeout: 5000,
    prepareHeaders: (headers) => {
      const token = getToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  });

export const toDataUrl: (url: string) => Promise<unknown> = async function (
  url: string,
) {
  //Convert to base64
  const res = await fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      return response.blob();
    })
    .then((blob) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (): unknown => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    })
    .catch((error) => {
      throw error;
    });

  return res;
};

/**
 * @description it takes the object and converts it to URLSearchParams string
 * @param {Record<string, any>} obj - The object to be converted
 * @returns {string} - The URLSearchParams string
 */
export function objectToURLSearchParams(obj: Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    if (value) {
      params.append(key, value.toString());
    }
  });

  if (params) {
    return `?${params.toString()}`;
  }

  return "";
}

/**
 * @description it takes start and end date and return range of dates between them
 * @param {filterItem[]} list - array of filterItem
 * @returns {object} - label and value list for select
 */
export const getList = (
  list: filterItem[] | [],
): { label: string; value: string }[] => {
  return list?.map((item) => ({
    label: item?.label || "",
    value: item?.id || "",
  }));
};

export const hslToRgb = (h: number, s: number, l: number): number[] => {
  let r, g, b;

  if (s == 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 3) {
        return q;
      }
      if (t < 1 / 2) {
        return q + (p - q) * 6 * (2 / 3 - t);
      }

      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r, g, b];
};

/**
 * @description convert the unique rgb color
 * @param {number} numColors number of the color to generate
 * @returns  {RGB[]} array of array rgb data
 */
export const generateUniqueColors = (numColors: number): RGB[] => {
  const colors = [];
  const saturation = 0.7;
  const lightness = 0.4;
  const goldenRatioConjugate = 0.618033988749895;
  let hue = 0.02;
  for (let i = 0; i < numColors; i++) {
    hue += goldenRatioConjugate;
    hue %= 1;
    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    colors.push(rgb(Math.abs(r), Math.abs(g), Math.abs(b)));
  }

  return colors;
};

export const getFileSize = (size: number): string => {
  if (size === 0) {
    return "0 bytes";
  }
  const k = 1024;
  const sizes = ["bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(size) / Math.log(k));

  return parseFloat((size / Math.pow(k, i)).toFixed(3)) + " " + sizes[i];
};

export const tableUpdateToast = (
  data: SocketData,
  msg: string,
  id?: string,
): void => {
  if (data?.userId !== getRootUserId() && data?.action) {
    customToast.success(
      msg,
      {
        position: "bottom-left",
      },
      id,
    );
  }
};

export const formatFinancialValue = (
  value: number,
  fixedPoint: number = 2,
  currencyType?: TCurrency,
): string => {
  if (currencyType) {
    return `${getCurrencySymbol(currencyType)}${value?.toFixed(fixedPoint)}`;
  }

  return value?.toFixed(fixedPoint);
};

//eslint-disable-next-line
/**
 * Maps an array of objects to select options with strict typing.
 * This is useful for transforming API data into options suitable for select components.
 * @template T The type of objects in the input array
 * @template [K=string] The type of the value property (defaults to string)
 * @param {Array<T> | undefined} data Input array of objects or undefined
 * @param {object} mapping Configuration for mapping properties
 * @param {keyof T} mapping.valueKey Property to use for option value
 * @param {keyof T} mapping.labelKey Property to use for option label
 * @param {function(T): string} [mapping.formatLabel] Optional label formatter
 * @param {function(T[keyof T]): K} [mapping.formatValue] Optional value formatter
 * @returns {Array<{label: string, value: K}>} Array of select options
 * @example
 * const users = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }];
 * const options = mapToSelectOptions(users, {
 *   valueKey: 'id',
 *   labelKey: 'name'
 * });
 * // Returns [{ label: 'Alice', value: 1 }, { label: 'Bob', value: 2 }]
 */
export function mapToSelectOptions<T, K = string>(
  data: T[] | undefined,
  mapping: {
    valueKey: keyof T;
    labelKey?: keyof T;
    labelRender?: (value: T) => string;
  },
  config?: {
    customKeys?: {
      label?: string;
      value?: string;
    };
  },
): Record<string, string | K>[] | { label: string; value: K }[] {
  if (!data?.length) {
    return [];
  }

  const dataToReturn = data.map((item) => {
    const rawValue = item[mapping.valueKey];
    const rawLabel = mapping.labelKey ? item[mapping.labelKey] : undefined;

    return {
      label: mapping.labelRender
        ? mapping.labelRender(item)
        : String(rawLabel ?? ""),
      value: (rawValue as unknown as K) ?? "",
    };
  });
  if (config?.customKeys) {
    const labelKey = config?.customKeys?.label ?? "label";
    const valueKey = config?.customKeys?.value ?? "value";

    return dataToReturn?.map((d) => {
      return {
        [labelKey]: d.label,
        [valueKey]: d.value,
      };
    });
  }

  return dataToReturn;
}
